/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import moment from 'moment';
import clsx from 'clsx';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { LocaleUtils } from 'react-day-picker';

import { eventUtil } from '@marriott/mi-ui-library-shop';
import { getLocalizedDate, TabComponent } from '@marriott/mi-ui-library';
import { CN_FLEXIBLE_FORMAT_YEAR_MONTH, isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';

import {
  ADD_BUDDHIST_YEAR_VALUE,
  MONTHSHORTVALUE,
  WEEKDAYSLONGNAME,
  WEEKSDAYSSHORT,
} from '../../../../../constants/CommonConstants';

import { ViewportContext, FormValuesContext, FocusContext, ContentContext } from '../../../index.contexts';
import { DATES } from '../../../constants/StoreConstants';
import { setDates } from '../../../store/store.actions';
import {
  CALENDAR_MAX_ALLOWED_DAYS,
  CN_YEAR_LABEL,
  KO_YEAR_LABEL,
  VARIATION_DESKTOP,
  FLEXIBLE_FORMAT_YEAR_MONTH,
  START_DATE,
  END_DATE,
  DATE_CLICK_TRACKING_LOCATION,
  DATE_LABEL_EXACT,
  DATE_LABEL_FLEXIBLE,
  CALENDER_LOAD_STATE_EXPANDED,
} from '../../../constants/ApplicationConstants';
import {
  DateObjectType,
  getMinStayNextObject,
  getMinStayPrevObject,
  getCustomFormattedDate,
  getCurrentDateObject,
  convertToUpperCase,
} from '../../../utils/DateUtils';
import CommonUtils from '../../../utils/CommonUtils';

import Popup from '../../atoms/Popup/Popup';
import PopupHeader from '../../atoms/PopupHeader/PopupHeader';
import PopupMain from '../../atoms/PopupMain/PopupMain';
import PopupFooter from '../../atoms/PopupFooter/PopupFooter';
import TertiaryCta from '../../atoms/TertiaryCta';
import { DatesHeader } from '../../atoms/DatesHeader/DatesHeader';

import Calendar from '../Calendar';
import DatePickerFlexible from '../Datepicker.flexible.tsx';

import { AddDatesProps, FocusedInputType } from './AddDates.types';
import { StyledAddDatesDiv, StyledDatesFooter } from './AddDates.styles';

const AddDates: FC<AddDatesProps> = ({
  onCancel,
  onChange: changeMobileState,
  isHideFlexibleDates,
  acceptLanguage = 'en-US',
  isMPO = false,
  colorScheme,
  model,
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const deepLinkParam = ['selectedCity', 'selectedStateProvince', 'selectedCountry'];
  const hasDeepLink = deepLinkParam.some(param => urlParams.has(param));
  const [isFlexibleTab, setFlexibleTab] = useState(() => {
    // Retain and reuse session values after page refresh or when returning to the page
    const storedValue = window.sessionStorage.getItem('mpoOffersFlexibleDates');
    return storedValue === 'true' || hasDeepLink;
  });
  const [currentActiveTab, setCurrentActiveTab] = useState<string>(() =>
    isFlexibleTab ? DATE_LABEL_FLEXIBLE : DATE_LABEL_EXACT
  );

  const handleSpecificDate = () => {
    window.sessionStorage.setItem('mpoOffersFlexibleDates', 'false');
  };

  /** Contexts */
  const isMobileView = useContext(ViewportContext);
  const { formValues, setFormValues, setIsPopupOpen, maxCalendarDays } = useContext(FormValuesContext);
  const { focusComp } = useContext(FocusContext);
  const YEAR_LABEL =
    acceptLanguage === 'zh-CN' || acceptLanguage === 'ja-JP'
      ? CN_YEAR_LABEL
      : acceptLanguage === 'ko-KR'
      ? KO_YEAR_LABEL
      : '';

  const {
    firstDayOfWeek,
    checkInLabel,
    checkOutLabel,
    weekdaysShort,
    weekdaysLong,
    months,
    monthsShort,
    dates,
    night,
    nights,
    dateFormat,
    yearLabel = YEAR_LABEL,
    datesPlaceholderText,
    exactDatesTabText,
    flexibleTabText,
    flexibleDatesSubmitCta,
    submitSpecificDateCta,
    flexibleDatesResetCta,
    resetSpecificDateCta,
    isAuthorMode,
    howManyNightsText,
    flexibleDateFormatLabel,
  } = useContext(ContentContext);

  const flexibleLabel = useContext(ContentContext)['flexible'];
  const tabList = [
    { tabTitle: exactDatesTabText, tabSubtitle: '', tabValue: DATE_LABEL_EXACT, isActive: true },
    { tabTitle: flexibleTabText, tabSubtitle: '', tabValue: DATE_LABEL_FLEXIBLE, isActive: false },
  ];

  /** States */
  const [popupOpenState, setPopupOpenState] = useState(false);
  const [focusedState, setFocusedState] = useState<FocusedInputType>(START_DATE);
  // if from and to dates not available then show 'Check in - Check out' else "fromDate - toDate"
  const [displayText, setDisplayText] = useState<string>(`${checkInLabel} - ${checkOutLabel}`);
  const [checkInDate, setCheckInDate] = useState(datesPlaceholderText);
  const [checkOutDate, setCheckOutDate] = useState(datesPlaceholderText);

  const compRef = useRef<HTMLDivElement>(null);
  const { fromDate, toDate, flexible, minimumStay, checkin, checkout, lastDefaultDate } = formValues?.[DATES] ?? {};
  const noOfNights = CommonUtils.getNumberOfNights(fromDate, toDate);
  const datesVal = {
    fromDate: fromDate,
    toDate: toDate,
    numberOfNights: noOfNights <= 9 ? noOfNights : minimumStay,
  };

  const numberOfNights = CommonUtils.getNumberOfNights(fromDate, toDate);
  let labelText = dates ? dates : '';
  if (numberOfNights > 0) {
    labelText = `${numberOfNights} ${numberOfNights > 1 ? nights : night}${flexible ? ` (${flexibleLabel})` : ''}`;
  }
  const labelTextMobile = flexible ? `${flexibleTabText}` : `${numberOfNights} ${numberOfNights > 1 ? nights : night}`;
  const onTabChange = (currTab: string): void => {
    if (currTab === 'exactDate') {
      setFormValues(
        setDates({
          ...(formValues?.[DATES] ?? {}),
          isSelectedDate: true,
          flexible: null,
        })
      );
      setFlexibleTab(false);
    } else {
      setFormValues(
        setDates({
          ...(formValues?.[DATES] ?? {}),
          fromDate: numberOfNights > 9 ? getCurrentDateObject() : formValues?.[DATES]?.fromDate,
          toDate:
            numberOfNights > 9
              ? getMinStayNextObject(getCurrentDateObject(), minimumStay || 1)
              : formValues?.[DATES]?.toDate,
          isSelectedDate: undefined,
          flexible: 'on',
        })
      );
      setFlexibleTab(true);
    }

    setCurrentActiveTab(currTab);
  };

  // if from and to dates not available then show 'Check in - Check out' else "fromDate - toDate"
  useEffect(() => {
    const formatDate = (date: any) =>
      getCustomFormattedDate(getLocalizedDate(date, dateFormat, acceptLanguage), acceptLanguage || 'en-US');

    const formatMonthYear = (date: any) =>
      convertToUpperCase(
        getLocalizedDate(
          date,
          isGreaterChinaLocale(acceptLanguage)
            ? CN_FLEXIBLE_FORMAT_YEAR_MONTH
            : flexibleDateFormatLabel || FLEXIBLE_FORMAT_YEAR_MONTH,
          acceptLanguage
        ),
        acceptLanguage as string
      );

    if (currentActiveTab === DATE_LABEL_FLEXIBLE || flexible === 'on') {
      if (!fromDate) {
        handleReset();
      } else {
        const formattedMonthYear = formatMonthYear(
          acceptLanguage === 'th-TH' ? moment(fromDate)?.add(ADD_BUDDHIST_YEAR_VALUE, 'years') : fromDate
        );
        setDisplayText(formattedMonthYear);
        setCheckInDate(formattedMonthYear);
        setCheckOutDate(datesPlaceholderText);
        setCurrentActiveTab(DATE_LABEL_FLEXIBLE);
        setFlexibleTab(true);
      }
    } else if (fromDate && toDate) {
      const formattedFrom = formatDate(fromDate);
      const formattedTo = formatDate(toDate);

      setDisplayText(`${formattedFrom} - ${formattedTo}`);
      setCheckInDate(formattedFrom);
      setCheckOutDate(formattedTo);
    } else if (fromDate) {
      const formattedFrom = formatDate(fromDate);

      setDisplayText(formattedFrom);
      setCheckInDate(formattedFrom);
      setCheckOutDate(datesPlaceholderText);
    } else {
      setDisplayText(`${checkInLabel} - ${checkOutLabel}`);
      setCheckInDate(datesPlaceholderText);
      setCheckOutDate(datesPlaceholderText);
    }
  }, [fromDate, toDate, currentActiveTab, flexible]);

  const [variation, setVariation] = React.useState<string>(CommonUtils.getVariation());

  let shortWeekdays = weekdaysShort?.split(/[،,]/);
  // set default weekdays if i18 weekdays value are invalid
  if (shortWeekdays?.length !== 7) {
    shortWeekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  }

  let weeksdaysShort = weekdaysShort?.split(/[،,]/);
  // set default weekdays if i18 weekdays value are invalid
  if (weeksdaysShort?.length !== 7) {
    weeksdaysShort = WEEKSDAYSSHORT;
  }

  let weekdaysLongName = weekdaysLong?.split(/[،,]/);
  if (weekdaysLongName?.length !== 7) {
    weekdaysLongName = WEEKDAYSLONGNAME;
  }

  let monthsValue = months?.split(/[،,]/);
  // set default month if i18 months value are invalid
  if (monthsValue?.length !== 12) {
    monthsValue = LocaleUtils.getMonths();
  }

  let monthsShortValue = monthsShort?.split(/[،,]/);
  // set default month if i18 months value are invalid
  if (monthsShortValue?.length !== 12) {
    monthsShortValue = MONTHSHORTVALUE;
  }

  const setLanguageLocalization = (): void => {
    moment.locale('locale', {
      months: monthsValue,
      monthsShort: monthsShortValue,
      weekdays: weekdaysLongName,
      weekdaysShort: weeksdaysShort,
    });
  };

  // Set toDate and fromDate values
  const setToDateAndFromDate = (): void => {
    let newFromDate = fromDate as DateObjectType;
    let newToDate = toDate as DateObjectType;
    if (!fromDate && toDate) {
      //if fromDate is not selected setting the day before minstay.
      newFromDate = getMinStayPrevObject(newToDate, minimumStay ?? 1);
    }
    if (!toDate && newFromDate) {
      //if toDate is not selected setting the next day after minstay.
      newToDate = getMinStayNextObject(newFromDate, minimumStay ?? 1) ?? null;
    }
    setDateHandler(newFromDate, newToDate);
  };

  // effect to handle the changes after popup state is set to open
  useEffect(() => {
    // to scroll search form to top on interaction
    setIsPopupOpen?.(!!popupOpenState);

    if (popupOpenState) {
      setFocusOnFirstBtn();
      setFocusedState(START_DATE);
    } else {
      setToDateAndFromDate();
      setLanguageLocalization();
    }
  }, [popupOpenState]);

  useEffect(() => {
    // To focus the first interactive element
    if (isMobileView) {
      CommonUtils.focusFirstTabbable(compRef);
    }
    if (model?.datePickerLayout === CALENDER_LOAD_STATE_EXPANDED && isAuthorMode === false) {
      showPopup();
    } else {
      setPopupOpenState(false);
    }
  }, []);

  useEffect(() => {
    // focus on the first interactive element in desktopview based on FocusContext API
    if (!isMobileView && focusComp === DATES) {
      CommonUtils.focusFirstTabbable(compRef);
    }
  }, [focusComp]);

  // to show popup on desktop on button click
  const showPopup = (): void => {
    setPopupOpenState(true);
  };

  // to show popup on desktop on button click
  const closePopup = (): void => {
    const sessionDateToSet = {
      startDate:
        formValues?.dates?.fromDate?.format('MM/DD/YYYY') ??
        getMinStayPrevObject(formValues?.dates?.toDate ?? moment(new Date()), minimumStay ?? 1)?.format('MM/DD/YYYY') ??
        null,
      endDate:
        formValues?.dates?.toDate?.format('MM/DD/YYYY') ??
        getMinStayNextObject(formValues?.dates?.fromDate ?? moment(new Date()), minimumStay ?? 1)?.format(
          'MM/DD/YYYY'
        ) ??
        null,
    };
    window.sessionStorage.setItem('OfferSearchDates', JSON.stringify(sessionDateToSet));
    if (flexible !== 'on') {
      window.sessionStorage.setItem('mpoOffersFlexibleDates', 'false');
    }
    let isInvalidSessionEndDate = false;
    if (sessionDateToSet?.startDate || sessionDateToSet?.endDate) {
      isInvalidSessionEndDate =
        moment(sessionDateToSet?.endDate) <
          moment(sessionDateToSet?.startDate).add(formValues?.dates?.minimumStay ?? 1, 'days') ||
        moment(sessionDateToSet?.endDate) > moment(formValues?.dates?.checkout);
    }
    isMPO
      ? eventUtil.dispatch('mpoInvalidSessionDateErrFlag', isInvalidSessionEndDate)
      : eventUtil.dispatch('invalidSessionDateErrFlag', isInvalidSessionEndDate);
    setPopupOpenState(false);
    compRef.current?.querySelector('button')?.focus();
  };

  const keyHandler = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (!e.shiftKey && e.keyCode === 9) {
      closePopup();
    }
  };
  const keyHandlerCalendarheader = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (e.shiftKey && e.keyCode === 9) {
      closePopup();
    }
  };

  // set focus on first button in the popup when popup state is set to open
  const setFocusOnFirstBtn = (): void => {
    const popupElemBtn = compRef.current?.querySelector('.search-dates-popup button') as HTMLElement;
    popupElemBtn?.focus({ preventScroll: true });
  };

  // handler to set fromDate in the formValues
  const setDateHandler = (startDate: DateObjectType | null, endDate: DateObjectType | null): void => {
    const from = startDate ?? undefined;
    let end = endDate ?? undefined;

    // if from and end date are same then remove end date and keep focus on end date
    if (from?.isSame(end)) {
      setFocusedState(END_DATE);
      // end = undefined;
      end = moment(new Date(checkin ?? '')).add(minimumStay ?? 1, 'days');
    }

    setFormValues(
      setDates({
        ...(formValues?.[DATES] ?? {}),
        fromDate: from,
        toDate: end,
        isSelectedDate: true,
      })
    );
  };

  // clean button handler on check in
  const handleClearCheckIn = (): void => {
    if (!isFlexibleTab) {
      setFocusedState(START_DATE);
      setDateHandler(null, null);
      window.sessionStorage.setItem('OfferSearchDates', '{}');
      window.sessionStorage.setItem('mpoOffersFlexibleDates', 'false');
    } else {
      handleReset();
    }
  };

  // clear button handler on checkout
  const handleClearCheckOut = (): void => {
    setFocusedState(END_DATE);
    setDateHandler(null, null);
    window.sessionStorage.setItem('OfferSearchDates', '{}');
    window.sessionStorage.setItem('mpoOffersFlexibleDates', 'false');
  };

  // handler for flexible dates
  const flexibleHandler = (startDate: DateObjectType | null, endDate: DateObjectType | null): void => {
    const from = startDate ?? undefined;
    let end = endDate ?? undefined;

    // if from and end date are same then remove end date and keep focus on end date
    if (from?.isSame(end)) {
      setFocusedState(END_DATE);
      end = moment(new Date(checkin ?? '')).add(minimumStay ?? 1, 'days');
    }
    window.sessionStorage.setItem('mpoOffersFlexibleDates', 'true');
    setFormValues(
      setDates({
        ...(formValues?.[DATES] ?? {}),
        fromDate: from,
        toDate: end,
        flexible: 'on',
      })
    );
  };

  // Utility to set variation based on true value
  const setSuccessVariation = (flag: boolean, variation: string): void => {
    if (flag) {
      setVariation(variation);
    }
  };

  //To update variation based on viewport dimensions change
  useEffect(() => {
    // set viewport change event and return callback function to clear events
    const updateViewportHandler = CommonUtils.addRemoveResizeHandler(setSuccessVariation);

    return (): void => {
      // remove change event listner on component unmount
      updateViewportHandler();
    };
  }, []);

  // on click done in mobile state
  const changeMobileStateOnDone = (): void => {
    setToDateAndFromDate();
    changeMobileState?.();
  };

  const calendarComp = () => {
    if (currentActiveTab === DATE_LABEL_EXACT) {
      return (
        <div
          className={clsx(
            isHideFlexibleDates && isMobileView ? 'datepicker-calendar-wrapper-mobile' : 'datepicker-calendar-wrapper',
            !isHideFlexibleDates ? 'datepicker-calendar-wrapper-multi-tabs' : ''
          )}
        >
          <Calendar
            startDate={fromDate ?? null}
            endDate={toDate ?? null}
            focusedInput={focusedState}
            setDateHandler={(startDate: DateObjectType | null, endDate: DateObjectType | null) => {
              setDateHandler(startDate, endDate);
              handleSpecificDate();
            }}
            setFocusedInputHandler={(newState: FocusedInputType): void => setFocusedState(newState)}
            variation={variation}
            nightLabel={night}
            nightsLabel={nights}
            maxAllowedDays={maxCalendarDays ?? CALENDAR_MAX_ALLOWED_DAYS}
            weekdays={shortWeekdays}
            months={monthsValue}
            weekdaysLong={weekdaysLongName}
            monthsShort={monthsShortValue}
            weekdaysShort={shortWeekdays}
            firstDayOfWeek={firstDayOfWeek as string}
            lengthOfStay={minimumStay}
            validOffersStartDate={moment(checkin)}
            validOffersEndDate={moment(checkout)}
            yearLabel={yearLabel}
            locale={acceptLanguage}
          />
        </div>
      );
    } else {
      return (
        <div className={'flexible-box'}>
          <DatePickerFlexible
            clickTrackingLoc={DATE_CLICK_TRACKING_LOCATION}
            dates={datesVal}
            onDateSelection={flexibleHandler}
            monthList={monthsValue}
            singleDateLimit={lastDefaultDate} // Display only the months that fall before or on the offer's end date
            maxNumberOfNights={model?.maxNumberOfNights || 9}
            howManyNightsText={howManyNightsText || 'How Many Night?'}
            minimumStay={minimumStay}
            isTabbedSearchForm={false}
            locale={acceptLanguage}
          />
        </div>
      );
    }
  };

  const handleReset = () => {
    if (currentActiveTab === DATE_LABEL_EXACT) {
      setFocusedState(START_DATE);
      setDateHandler(null, null);
    } else {
      flexibleHandler(getCurrentDateObject(), getMinStayNextObject(getCurrentDateObject(), minimumStay || 1));
    }
  };

  return (
    <StyledAddDatesDiv ref={compRef}>
      {!isMobileView ? (
        <button
          type="button"
          onClick={showPopup}
          aria-expanded={popupOpenState}
          className="hover-60 desktop-calendar-btn"
          data-testid="offers-adddates"
        >
          <span className="search__calendar-label icon-nav---book t-label-xs">{labelText}</span>
          <span
            className={`h-r-form-field-txt search__calendar-value${!fromDate && !toDate ? '-empty' : ''} ${
              acceptLanguage === 'it-IT' ? 'lowerCaseDate' : 'textTransformNone'
            }`}
          >
            {displayText}
          </span>
        </button>
      ) : (
        ''
      )}
      {isMobileView || popupOpenState ? (
        <Popup
          show={true}
          handleBlur={!isMobileView}
          popupOpenState={popupOpenState}
          setPopupOpenState={setPopupOpenState}
          className={`search-dates-popup ${colorScheme}`}
        >
          {isMobileView ? (
            <PopupHeader
              labelIcon="icon-nav---book"
              labelText={labelTextMobile}
              mainText={displayText}
              cancelClickHandler={isMobileView && onCancel ? onCancel : undefined}
              className="search-dates-popup__header"
            />
          ) : (
            <DatesHeader
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              onClearCheckIn={handleClearCheckIn}
              onClearCheckOut={handleClearCheckOut}
              focusedCheckIn={focusedState === START_DATE}
              onClickCheckIn={(): void => setFocusedState(START_DATE)}
              onClickCheckOut={(): void => setFocusedState(END_DATE)}
              acceptLanguage={acceptLanguage}
              keyHandlerCalendarheader={keyHandlerCalendarheader}
              nightsLabel={flexibleTabText}
              isFlexible={isFlexibleTab}
            />
          )}
          <PopupMain className={`search-dates-popup__main ${variation !== VARIATION_DESKTOP ? 'h-r-center-item' : ''}`}>
            <TabComponent
              tabList={tabList}
              setSelectedTab={onTabChange}
              selectedTab={currentActiveTab}
              clickTrackingLoc={DATE_CLICK_TRACKING_LOCATION}
              hidetabsAndContent={false}
              children={calendarComp()}
              isSwitcherTabs={true}
              customClass={currentActiveTab === DATE_LABEL_EXACT && isMobileView ? '' : 'datepicker-tab-wrapper'}
              disabledContainerClass={true}
              hideTabs={false}
              ariaRole="button"
            />
          </PopupMain>
          {isMobileView ? (
            <PopupFooter
              primaryBtnLabel={isFlexibleTab ? flexibleDatesSubmitCta : submitSpecificDateCta}
              primaryBtnClickHandler={changeMobileStateOnDone}
              resetClickHandler={handleReset}
              resetLabel={isFlexibleTab ? flexibleDatesResetCta : resetSpecificDateCta}
            />
          ) : (
            <StyledDatesFooter className="brand-css">
              {/* Flexible Reset */}
              <TertiaryCta
                clickHandler={handleReset}
                keyHandler={handleReset}
                label={isFlexibleTab ? flexibleDatesResetCta : resetSpecificDateCta}
                className="reset-btn t-text-color m-link-action"
              />
              <button
                type="button"
                onClick={closePopup}
                onKeyDown={keyHandler}
                className="m-button-primary brand-css"
                data-testid="doneBtn"
              >
                {isFlexibleTab ? flexibleDatesSubmitCta : submitSpecificDateCta}
              </button>
            </StyledDatesFooter>
          )}
        </Popup>
      ) : (
        ''
      )}
    </StyledAddDatesDiv>
  );
};

export default AddDates;
